import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Subscription } from '@rails/actioncable';
import { CompanyDayJobStat_Read, DispatchStatus } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';

import {
  LoadingSpinner,
  OverflowAwareText,
} from '~components/Order/ordersDispatchStyledComponents';
import { dispatchStatusLabelKeys, dispatchStatusTooltipKeys } from '~constants/dispatch';
import { CompanyDayJobStat } from '~hooks/useCompanyDayJobStat/models';
import useCompanyDayJobStat from '~hooks/useCompanyDayJobStat/useCompanyDayJobStat';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { getDispatchStatusDate } from '~store/OrdersDispatchStore';
import { subscribeToChannel } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

// Lets the pointer sweep down the list without a tooltip flashing on every row it crosses.
const SUMMARY_TOOLTIP_ENTER_DELAY_IN_MS = 300;

interface SummaryCountProps {
  dispatchStatus: DispatchStatus;
  getValue: (stat: CompanyDayJobStat) => number;
}

interface SummaryCountsProps {
  refreshToken?: number;
}

const notStartedSummaryCounts: SummaryCountProps[] = [
  { dispatchStatus: DispatchStatus.UNASSIGNED, getValue: (stat) => stat.unassignedCount },
  { dispatchStatus: DispatchStatus.ASSIGNED, getValue: (stat) => stat.assignedCount },
  { dispatchStatus: DispatchStatus.SENT, getValue: (stat) => stat.sentCount },
  { dispatchStatus: DispatchStatus.REJECTED, getValue: (stat) => stat.rejectedCount },
  { dispatchStatus: DispatchStatus.ACCEPTED, getValue: (stat) => stat.acceptedCount },
];

const inProgressSummaryCounts: SummaryCountProps[] = [
  { dispatchStatus: DispatchStatus.ENROUTE, getValue: (stat) => stat.enrouteCount },
  { dispatchStatus: DispatchStatus.LOADED, getValue: (stat) => stat.loadedCount },
  { dispatchStatus: DispatchStatus.UNLOADED, getValue: (stat) => stat.unloadedCount },
  { dispatchStatus: DispatchStatus.COMPLETED, getValue: (stat) => stat.completedCount },
  { dispatchStatus: DispatchStatus.CANCELED, getValue: (stat) => stat.canceledCount },
];

interface TopStatProps {
  label: string;
  tooltip: string;
  total: number;
  value: number;
}

function TopStat({ label, tooltip, total, value }: TopStatProps) {
  return (
    <Tooltip
      disableInteractive
      enterDelay={SUMMARY_TOOLTIP_ENTER_DELAY_IN_MS}
      title={tooltip}
      placement="bottom-start"
    >
      <Box
        sx={(theme) => ({
          borderRadius: '4px',
          boxShadow: `inset 0 0 0 1px ${theme.semantic.border}`,
          px: 1,
          py: 0.75,
        })}
      >
        <Typography
          sx={(theme) => ({
            color: theme.semantic.textSecondary,
            fontSize: '12px',
            fontWeight: 500,
          })}
        >
          {label}
        </Typography>
        <Box sx={{ alignItems: 'baseline', display: 'flex', gap: '4px' }}>
          <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>{value}</Typography>
          <Typography
            sx={(theme) => ({ color: theme.semantic.textSecondary, fontSize: '13px' })}
          >
            /
          </Typography>
          <Typography
            sx={(theme) => ({ color: theme.semantic.textSecondary, fontSize: '13px' })}
          >
            {total}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
}

interface StatusGroupProps {
  basis: string;
  counts: SummaryCountProps[];
  selectedStatuses: DispatchStatus[];
  stats: CompanyDayJobStat;
  title: string;
  onSelect: (dispatchStatus: DispatchStatus) => void;
}

function StatusGroup({
  basis,
  counts,
  onSelect,
  selectedStatuses,
  stats,
  title,
}: StatusGroupProps) {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Box sx={{ pb: 0.5, px: 1 }}>
        <Typography
          sx={(theme) => ({
            color: theme.semantic.textSecondary,
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.03em',
            lineHeight: 1.3,
            textTransform: 'uppercase',
          })}
        >
          {title}
        </Typography>
        <Typography
          variant={'caption'}
          sx={(theme) => ({
            color: theme.semantic.textSecondary,
            display: 'block',
            lineHeight: 1.3,
          })}
        >
          {basis}
        </Typography>
      </Box>
      {counts.map(({ dispatchStatus, getValue }) => (
        <Tooltip
          disableInteractive
          enterDelay={SUMMARY_TOOLTIP_ENTER_DELAY_IN_MS}
          key={dispatchStatus}
          title={t(dispatchStatusTooltipKeys[dispatchStatus])}
          placement="bottom-start"
        >
          <Box
            aria-pressed={selectedStatuses.includes(dispatchStatus)}
            role="button"
            onClick={() => onSelect(dispatchStatus)}
            sx={{
              alignItems: 'center',
              bgcolor: selectedStatuses.includes(dispatchStatus)
                ? theme.semantic.surfaceSunken
                : 'transparent',
              boxShadow: selectedStatuses.includes(dispatchStatus)
                ? `inset 0 0 0 1px ${theme.semantic.borderStrong}`
                : 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              px: 1,
              py: '2px',
              '&:hover': {
                bgcolor: selectedStatuses.includes(dispatchStatus)
                  ? theme.semantic.surfaceSunken
                  : theme.semantic.actionHover,
              },
            }}
          >
            <OverflowAwareText
              sx={{ fontWeight: selectedStatuses.includes(dispatchStatus) ? 600 : 400 }}
            >
              {t(dispatchStatusLabelKeys[dispatchStatus])}
            </OverflowAwareText>

            <Typography variant={'body2'} sx={{ flexShrink: 0 }}>
              {getValue(stats)}
            </Typography>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}

export const SummaryCounts = observer(({ refreshToken }: SummaryCountsProps) => {
  const theme = useTheme();
  const { ordersDispatchStore, userStore } = useStores();
  const { getCompanyDayJobStatsByCompanyIdAndDate } = useCompanyDayJobStat();
  const [companyDayJobStats, setCompanyDayJobStats] = useState<{
    isLoading: boolean;
    stats?: CompanyDayJobStat;
  }>({ isLoading: true });
  const [companyDayJobStatSubscription, setCompanyDayJobStatSubscription] =
    useState<Subscription>();

  const [areMetricDetailsVisible, setAreMetricDetailsVisible] = useState(true);

  const date = getDispatchStatusDate(ordersDispatchStore.filters);
  const companyId = getEffectiveUserCompanyId(userStore);

  const handleMetricDetailsDisplay = useCallback(() => {
    setAreMetricDetailsVisible((areVisible) => !areVisible);
  }, []);

  const handleStatClick = useCallback(
    (dispatchStatus: DispatchStatus) => {
      const appliedStatuses = ordersDispatchStore.filters.dispatchStatuses ?? [];
      const nextStatuses = appliedStatuses.includes(dispatchStatus)
        ? appliedStatuses.filter((appliedStatus) => appliedStatus !== dispatchStatus)
        : [...appliedStatuses, dispatchStatus];

      ordersDispatchStore.setFilters(
        {
          dispatchStatuses: nextStatuses.length ? nextStatuses : undefined,
          jobStates: undefined,
        },
        true,
      );
    },
    [JSON.stringify(ordersDispatchStore.filters.dispatchStatuses)],
  );

  const subscribeToCompanyDayJobStatRTU = useCallback(() => {
    subscribeToChannel(
      realTimeChannels.CompanyDayJobStatChannel,
      companyId,
      setCompanyDayJobStatSubscription,
      ({ data }: { data: CompanyDayJobStat_Read }) => {
        setCompanyDayJobStats({ isLoading: false, stats: CompanyDayJobStat.parse(data) });
      },
      { day: date },
    );
  }, [companyId, date]);

  useEffect(() => {
    subscribeToCompanyDayJobStatRTU();

    return () => {
      companyDayJobStatSubscription?.unsubscribe();
    };
  }, [companyDayJobStatSubscription, subscribeToCompanyDayJobStatRTU]);

  useEffect(() => {
    setCompanyDayJobStats({ isLoading: true });

    getCompanyDayJobStatsByCompanyIdAndDate({ companyId, date })
      .then((stats) => {
        setCompanyDayJobStats({ isLoading: false, stats });
      })
      .catch(() => {
        setCompanyDayJobStats({ isLoading: false });
      });
  }, [date, companyId, refreshToken]);

  useEffect(() => {
    ordersDispatchStore.setOrdersCount({
      ordersTotal: companyDayJobStats.stats?.ordersTotalCount ?? 0,
      ordersPendingRequest: companyDayJobStats.stats?.ordersPendingRequestCount ?? 0,
      ordersExternal: companyDayJobStats.stats?.ordersExternalCount ?? 0,
      ordersExternalOnly: companyDayJobStats.stats?.ordersExternalOnlyCount ?? 0,
    });
  }, [companyDayJobStats.stats]);

  // The stats are fetched for a single day, so they'd misrepresent a range or All Dates.
  if (!ordersDispatchStore.isFetchingOrdersForASingleDay) {
    return null;
  }

  if (companyDayJobStats.isLoading) {
    return (
      <Box sx={{ mt: 1.5 }}>
        <LoadingSpinner isVisible />
      </Box>
    );
  }

  if (!companyDayJobStats.stats) {
    return null;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          columnGap: 1,
          pb: 1,
        }}
      >
        <TopStat
          label={t('dispatch.summary_counts.jobs_accepted')}
          tooltip={t('dispatch.summary_counts.tooltips.jobs_accepted')}
          total={companyDayJobStats.stats.totalCount}
          value={companyDayJobStats.stats.hasBeenAcceptedCount}
        />
        <TopStat
          label={t('dispatch.summary_counts.trucks_available')}
          tooltip={t('dispatch.summary_counts.tooltips.trucks_available')}
          total={companyDayJobStats.stats.capacityTotalCount}
          value={companyDayJobStats.stats.capacityAvailableCount}
        />
      </Box>
      <Collapse in={areMetricDetailsVisible} timeout={400} easing={'ease'}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            columnGap: 1,
            pb: 1.5,
          }}
        >
          <StatusGroup
            basis={t('dispatch.summary_counts.not_started_basis')}
            counts={notStartedSummaryCounts}
            onSelect={handleStatClick}
            selectedStatuses={ordersDispatchStore.filters.dispatchStatuses ?? []}
            stats={companyDayJobStats.stats}
            title={t('dispatch.summary_counts.not_started')}
          />
          <StatusGroup
            basis={t('dispatch.summary_counts.in_progress_basis')}
            counts={inProgressSummaryCounts}
            onSelect={handleStatClick}
            selectedStatuses={ordersDispatchStore.filters.dispatchStatuses ?? []}
            stats={companyDayJobStats.stats}
            title={t('dispatch.summary_counts.in_progress')}
          />
        </Box>
      </Collapse>
      <ButtonBase
        aria-label={
          areMetricDetailsVisible
            ? t('dispatch.summary_counts.show_less')
            : t('dispatch.summary_counts.show_more')
        }
        onClick={handleMetricDetailsDisplay}
        sx={{
          borderRadius: '4px',
          color: theme.semantic.textSecondary,
          display: 'flex',
          height: '20px',
          justifyContent: 'center',
          width: '100%',
          '&:hover': { bgcolor: theme.semantic.actionHover },
        }}
      >
        {areMetricDetailsVisible ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </ButtonBase>
    </Box>
  );
});
